/**
 * CellShell wraps every editable cell with the shared error-state visuals:
 *  - bg-destructive-soft when the cell has a required-field error
 *  - bg-warning-soft when the cell has an optional-field error
 *  - Tooltip exposing the field-level error message when active
 *
 * The `wrapperClassName` slot lets cell-type-specific descendant overrides
 * live with the cell that needs them (e.g. SelectCell hides SimpleSelect's
 * internal label via `[&_label]:hidden`).
 *
 * The `value` slot drives a truncation tooltip: when a cell's content is
 * visually clipped in a narrow column — an ellipsis-truncated select trigger
 * (e.g. "No Access Needed") or an overflow-scrolled input — hovering surfaces
 * the full value in the same shadcn Tooltip used for error messages. Overflow
 * is measured lazily on hover (no ResizeObserver), so there's no per-cell
 * observer or render-loop cost on a large table.
 */

import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface CellShellProps {
  isActive: boolean;
  isOptionalField?: boolean;
  errorMessage?: string;
  wrapperClassName?: string;
  /** Full cell value; shown in a tooltip when the cell is truncated. */
  value?: string | null;
  children: ReactNode;
}

const getErrorBgClass = (
  isActive: boolean,
  isOptionalField: boolean | undefined,
): string => {
  if (!isActive) return '';
  return isOptionalField ? 'bg-warning-soft' : 'bg-destructive-soft';
};

/**
 * The element that actually clips the value differs by cell type: text/number/
 * date/zip cells clip inside their `<input>`; the select cell clips inside the
 * truncating `<span>` within its combobox button (the button itself never
 * overflows — it's full-width and the span shrinks).
 */
const getOverflowTarget = (root: HTMLElement | null): Element | null => {
  if (!root) return null;
  return (
    root.querySelector('input, textarea') ??
    root.querySelector('[role="combobox"] span')
  );
};

const isOverflowing = (el: Element | null): boolean =>
  !!el && el.scrollWidth > el.clientWidth;

const CellShell = ({
  isActive,
  isOptionalField,
  errorMessage,
  wrapperClassName,
  value,
  children,
}: CellShellProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const hasError = isActive && Boolean(errorMessage);
  // Empty / placeholder cells carry no readable value.
  const fullValue = value && value !== '-' ? value : null;

  const sectionClass = cn(
    'flex h-full items-center px-1',
    getErrorBgClass(isActive, isOptionalField),
    wrapperClassName,
  );

  // Nothing could ever be shown → skip the tooltip machinery entirely.
  if (!hasError && !fullValue) {
    return <section className={sectionClass}>{children}</section>;
  }

  const measureTruncation = (): void => {
    setIsTruncated(isOverflowing(getOverflowTarget(sectionRef.current)));
  };

  // The error message takes priority over the truncated-value hint.
  const tooltipContent = hasError
    ? errorMessage
    : isTruncated
      ? fullValue
      : null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <section
            ref={sectionRef}
            onPointerEnter={measureTruncation}
            className={sectionClass}
          >
            {children}
          </section>
        </TooltipTrigger>
        {tooltipContent && <TooltipContent>{tooltipContent}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};

export default CellShell;
export { getOverflowTarget, isOverflowing };
